import { useEffect, useState } from 'react';
import { Form, Modal, Alert } from 'react-bootstrap';
import { IconButton } from '@tradesolution/iceberg-ui-react';
import ByttFakturaAdresseForm from './Forms/ByttFakturaAdresse';
import ByttPostAdresseForm from './Forms/ByttPostAdresse';
import {
  ByttAdresseCommand,
  ByttFakturaEpostCommand,
  ByttKontonummerCommand,
  FlyttEnheterTilKjedeCommand,
  FlyttEnheterTilEnhetsgruppeCommand,
  FlyttEnheterTilNyHovedgrossistCommand,
  MeldEnheterUtAvEnhetsgrupperingCommand,
  MeldEnheterUtvalgInnEnhetsgrupperingCommand,
} from 'services/KjederegisteretAdminApi/EnheterBulkApi/types';
import EnheterBulkApi from 'services/KjederegisteretAdminApi/EnheterBulkApi';
import ByttFakturaEpostForm from './Forms/ByttFakturaEpost';
import ByttKontonummerForm from './Forms/ByttKontoNummer';
import ByttHovedGrossistForm from './Forms/ByttHovedGrossist';
import FlyttEnheterTilKjedeForm from './Forms/FlyttEnheterTilKjede';
import FlyttEnheterTilEnhetsgruppeForm from './Forms/FlyttEnheterTilEnhetsgruppe';
import MeldEnheterUtAvEnhetsgrupperingForm from './Forms/MeldUtAvEnhetsgruppering';
import MeldEnheterInnIEnhetsgrupperingForm from './Forms/MeldInnIEnhetsgruppering';

interface Props {
  show: boolean;
  selectedLopeNr: number[];
  onClose: () => void;
}

class BulkJob<TCommand> {
  id: number;
  name: string;
  description: string;
  command: TCommand;
  handleCommand: (command: TCommand) => Promise<void>;

  constructor(
    id: number,
    name: string,
    description: string,
    command: TCommand,
    handleCommand: (command: TCommand) => Promise<void>,
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.command = command;
    this.handleCommand = handleCommand;
  }
}

const BulkJobs = [
  new BulkJob<ByttAdresseCommand>(
    0,
    'Bytt fakturaadresse',
    'Bytt fakturaadresse for valgte enheter',
    {
      gateadresse: '',
      postnummer: '',
      adressetype: 'FakturaAdresse',
      ekstraInfo: '',
      ignoreAdresse: false,
      lopenr: [],
      // legger til begge nøklene pga backend-validering
      lopenummer: [],
    } as any,
    async (command) => {
      await EnheterBulkApi.byttAdresse(command);
    },
  ),
  new BulkJob<ByttAdresseCommand>(
    8,
    'Bytt postadresse',
    'Bytt postadresse for valgte enheter',
    {
      gateadresse: '',
      postnummer: '',
      // Bruk 'Postadresse' (uten stor A i Adresse-delen) i henhold til bakend-krav
      adressetype: 'Postadresse',
      ekstraInfo: '',
      ignoreAdresse: false,
      lopenr: [],
      lopenummer: [],
    } as any,
    async (command) => {
      console.debug('Sender bytt postadresse kommando', command);
      await EnheterBulkApi.byttAdresse(command);
    },
  ),
  new BulkJob<ByttFakturaEpostCommand>(
    1,
    'Bytt fakturaepost',
    'Bytt fakturaepost for valgte enheter',
    {
      epost: '',
    },
    async (command) => {
      await EnheterBulkApi.byttFakturaEpost(command);
    },
  ),
  new BulkJob<ByttKontonummerCommand>(
    2,
    'Bytt kontonummer',
    'Bytt kontonummer for valgte enheter',
    {
      kontonummer: '',
    },
    async (command) => {
      await EnheterBulkApi.byttKontonummer(command);
    },
  ),
  new BulkJob<FlyttEnheterTilNyHovedgrossistCommand>(
    3,
    'Flytt til ny hovedgrossist',
    'Flytt valgte enheter til ny hovedgrossist',
    {
      grossistNummer: 0,
      overforKundenummer: false,
    },
    async (command) => {
      await EnheterBulkApi.flyttTilNyHovedgrossist(command);
    },
  ),
  new BulkJob<FlyttEnheterTilKjedeCommand>(
    4,
    'Flytt til ny kjede',
    'Flytt valgte enheter til ny kjede',
    {
      kjedeNummer: 0,
      fraOgMed: new Date(),
      overforKundenummer: false,
    },
    async (command) => {
      await EnheterBulkApi.flyttEnheterTilKjede(command);
    },
  ),
  new BulkJob<MeldEnheterUtAvEnhetsgrupperingCommand>(
    5,
    'Meld ut av enhetsgruppering',
    'Meld valgte enheter ut av enhetsgruppering. Enheter med aktiv tilknyting til valgt enhetsgruppe vil få satt til og med dato på kobling. Enheter uten aktiv tilknytning vil ignoreres',
    {
      meldUtAvEnhetsgrupperingNummer: -1,
      lopenummer: [],
      tilOgMedDato: new Date(),
    },
    async (command) => {
      await EnheterBulkApi.meldEnheterUtAvEnhetsgruppering(command);
    },
  ),
  new BulkJob<MeldEnheterUtvalgInnEnhetsgrupperingCommand>(
    6,
    'Meld inn i enhetsgruppering',
    'Meld valgte enheter inn i enhetsgruppering. Enheter med aktiv tilknytning i enhetsgruppe med SBSK vil ignoreres',
    {
      meldInnIEnhetsgrupperingNummer: -1,
      lopenummer: [],
      fraOgMedDato: new Date(),
      skalBetraktesSomKjedemedlemskap: false,
    },
    async (command) => {
      await EnheterBulkApi.meldEnheterInnIEnhetsgruppering(command);
    },
  ),
  new BulkJob<FlyttEnheterTilEnhetsgruppeCommand>(
    7,
    'Flytt enheter til enhetsgruppering',
    'Valgte enheter som ikke ligger i fra enhetsgruppe vil bli ignorert. Hvis medlemskapet er skal betraktes som kjedemedlemskap i fra enhetsgruppe vil de også bli det i til enhetsgruppen',
    {
      fraOgMed: new Date(),
      fraEnhetsgruppeNummer: 0,
      tilEnhetsgruppeNummer: 0,
      overforKundenummer: false,
    },
    async (command) => {
      await EnheterBulkApi.flyttEnheterTilEnhetsgruppe(command);
    },
  ),
];

const BulkJobModal = (props: Props) => {
  const [selectedJobId, setSelectedJobId] = useState<number>(-1);
  const [selectedJob, setSelectedJob] = useState<BulkJob<any>>();

  useEffect(() => {
    setSelectedJob(BulkJobs.find((job) => job.id === selectedJobId));
    setJobStatus(JobStatus.NotStarted);
  }, [selectedJobId]);

  // Håndter inkonsekvent navngiving ("lopenr" vs "lopenummer") i ulike kommandoer
  const setLopenrOnCommand = (cmd: any, values: number[]) => {
    if (!cmd) return cmd;
    const updated: any = { ...cmd };
    // Sett begge for sikkerhets skyld dersom backend kun leser en av dem
    updated.lopenr = values; // definert i ByttAdresseCommand
    updated.lopenummer = values; // andre kommandoer
    return updated;
  };

  useEffect(() => {
    setSelectedJob((prev) =>
      prev ? { ...prev, command: setLopenrOnCommand(prev.command, props.selectedLopeNr) } : prev,
    );
  }, [props.selectedLopeNr]);

  const handleCloseModal = () => {
    setSelectedJobId(undefined);
    props.onClose();
  };

  enum JobStatus {
    NotStarted,
    Started,
    Finished,
    Error,
  }

  const [jobStatus, setJobStatus] = useState<JobStatus>(JobStatus.NotStarted);

  const handleSubmit = async () => {
    if (!props.selectedLopeNr || props.selectedLopeNr.length === 0) {
      setJobStatus(JobStatus.Error);
      console.warn('Ingen løpenr valgt – aborterer kall.');
      return;
    }
    try {
      setJobStatus(JobStatus.Started);
      const cmdWithLopenr = setLopenrOnCommand(selectedJob.command, props.selectedLopeNr);
      console.debug('Sender bulk kommando', selectedJobId, cmdWithLopenr);
      await selectedJob.handleCommand(cmdWithLopenr);
      setJobStatus(JobStatus.Finished);
    } catch (error) {
      console.error(error);
      setJobStatus(JobStatus.Error);
    }
  };

  // Extra validation for specific jobs
  const isFlyttEnheterTilEnhetsgruppeInvalid =
    selectedJobId === 7 &&
    !!selectedJob &&
    (!selectedJob.command?.fraEnhetsgruppeNummer ||
      !selectedJob.command?.tilEnhetsgruppeNummer ||
      selectedJob.command?.fraEnhetsgruppeNummer === selectedJob.command?.tilEnhetsgruppeNummer);

  // Ekstra validering for adresse-jobber: må ha gateadresse+postnummer når ignoreAdresse=false
  const isAdresseJob = selectedJobId === 0 || selectedJobId === 8;
  const adresseInvalid =
    isAdresseJob &&
    !!selectedJob &&
    !selectedJob.command.ignoreAdresse &&
    (!selectedJob.command.gateadresse || !selectedJob.command.postnummer);

  const submitDisabled =
    selectedJobId === -1 ||
    jobStatus === JobStatus.Started ||
    isFlyttEnheterTilEnhetsgruppeInvalid ||
    adresseInvalid;

  // console.debug('Selected bulk job', selectedJob);

  return (
    <Modal show={props.show} onHide={handleCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>Bulk endringer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Det er valgt {props.selectedLopeNr.length} enheter</p>
        <Form.Group className="mb-3">
          <Form.Label>Bulkjobb</Form.Label>
          <Form.Select
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(Number(e.target.value))}
          >
            <option value="-1">Velg jobb</option>
            {BulkJobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        {selectedJob?.description && <p>{selectedJob.description}</p>}
        {selectedJobId === 0 && !!selectedJob && (
          <ByttFakturaAdresseForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 8 && !!selectedJob && (
          <ByttPostAdresseForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 1 && !!selectedJob && (
          <ByttFakturaEpostForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 2 && !!selectedJob && (
          <ByttKontonummerForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 3 && !!selectedJob && (
          <ByttHovedGrossistForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 4 && !!selectedJob && (
          <FlyttEnheterTilKjedeForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 5 && !!selectedJob && (
          <MeldEnheterUtAvEnhetsgrupperingForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 6 && !!selectedJob && (
          <MeldEnheterInnIEnhetsgrupperingForm
            command={selectedJob.command}
            onCommandUpdated={(c) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: c } : prev))
            }
          />
        )}
        {selectedJobId === 7 && !!selectedJob && (
          <FlyttEnheterTilEnhetsgruppeForm
            command={selectedJob.command}
            onCommandUpdated={(update) =>
              setSelectedJob((prev) => (prev ? { ...prev, command: update(prev.command) } : prev))
            }
          />
        )}

        {isFlyttEnheterTilEnhetsgruppeInvalid && (
          <Alert variant="warning" style={{ marginTop: '0.5rem' }}>
            Velg både fra- og til enhetsgruppering, og de kan ikke være like.
          </Alert>
        )}
        {adresseInvalid && (
          <Alert variant="warning" style={{ marginTop: '0.5rem' }}>
            Fyll inn adresse og postnummer eller velg "Ignorer adresse".
          </Alert>
        )}

        {jobStatus === JobStatus.Started && (
          <Alert variant="info" style={{ marginTop: '0.5rem' }}>
            Jobben er startet. Hvis den tar lenger enn 1 min vil du få tilsendt resultatet via
            epost. <a href="/bulkjobber">Oversikt over kjørte bulkjobber finner du her</a>
          </Alert>
        )}

        {jobStatus === JobStatus.Finished && (
          <Alert variant="success" style={{ marginTop: '0.5rem' }}>
            Jobben er ferdig. <a href="/bulkjobber">Resultatet finner du her</a>
          </Alert>
        )}

        {jobStatus === JobStatus.Error && (
          <Alert variant="danger" style={{ marginTop: '0.5rem' }}>
            En feil oppstod under kjøring av jobben. Kontroller at enheter er valgt og prøv igjen.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {jobStatus === JobStatus.Finished && (
          <IconButton
            icon="close"
            variant="outline-primary"
            className="col"
            onClick={props.onClose}
          >
            Lukk
          </IconButton>
        )}
        {jobStatus != JobStatus.Finished && (
          <>
            <IconButton
              icon="close"
              variant="outline-primary"
              className="col"
              onClick={props.onClose}
            >
              Avbryt
            </IconButton>
            <IconButton
              className="col"
              icon="disk"
              variant="primary"
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              Utfør
            </IconButton>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkJobModal;
